/**
 * Canonical encoding helpers for the bulletin transcript.
 *
 * The verifier and the producer must encode identically, byte-for-byte —
 * any divergence between the two breaks signature verification and
 * causes valid transcripts to fail. Centralize all encoding here so
 * there is exactly one canonical form to audit.
 *
 * No secrets pass through these helpers; they are pure data.
 *
 * STATUS: M1 (registrar). Surface is intentionally minimal — DKG,
 * voter, trustee, tally extensions land in subsequent milestones.
 */
import { ristretto255 } from "@noble/curves/ed25519";
import { sha256 as nobleSha256 } from "@noble/hashes/sha2.js";

import type { RistrettoPoint, Scalar } from "../types";

/**
 * Order of the Ristretto255 prime-order group. Read from `@noble/curves`
 * at module load to avoid a hardcoded constant drifting from the library.
 */
export const GROUP_ORDER: bigint = ristretto255.Point.Fn.ORDER;

/**
 * Sample a uniform scalar in [1, GROUP_ORDER - 1] using the universal
 * `crypto.getRandomValues` API — works in modern Node (≥ 19) and every
 * browser, so the same primitive runs in the registrar CLI on a
 * laptop and in the voter's browser.
 *
 * Implementation: 64 random bytes reduced mod n; statistical distance
 * from uniform < 2^-256. Rejects 0 (degenerate; signing key would
 * always sign to identity).
 */
export function randomScalar(): Scalar {
  while (true) {
    const buf = new Uint8Array(64);
    globalThis.crypto.getRandomValues(buf);
    let acc = 0n;
    for (let i = 0; i < 64; i++) {
      acc = (acc << 8n) | BigInt(buf[i]!);
    }
    const s = acc % GROUP_ORDER;
    if (s !== 0n) return s;
  }
}

/**
 * Encode a scalar as 32 little-endian bytes (Curve25519 / Ristretto
 * convention).
 *
 * Rejects out-of-range inputs early so a buggy caller cannot smuggle a
 * larger-than-order scalar through to the curve arithmetic, where it
 * would silently alias to its reduction.
 */
export function encodeScalar(s: Scalar): Uint8Array {
  if (s < 0n || s >= GROUP_ORDER) {
    throw new Error("scalar out of range");
  }
  const out = new Uint8Array(32);
  let acc = s;
  for (let i = 0; i < 32; i++) {
    out[i] = Number(acc & 0xffn);
    acc >>= 8n;
  }
  return out;
}

/**
 * Decode 32 little-endian bytes into a scalar. Validates the value is
 * strictly less than GROUP_ORDER; out-of-range encodings are rejected.
 */
export function decodeScalar(bytes: Uint8Array): Scalar {
  if (bytes.length !== 32) {
    throw new Error(`scalar must be 32 bytes, got ${bytes.length}`);
  }
  let acc = 0n;
  for (let i = 31; i >= 0; i--) {
    acc = (acc << 8n) | BigInt(bytes[i]!);
  }
  if (acc >= GROUP_ORDER) {
    throw new Error("scalar exceeds group order");
  }
  return acc;
}

export function encodePoint(point: RistrettoPoint): Uint8Array {
  return point.toBytes();
}

export function decodePoint(bytes: Uint8Array): RistrettoPoint {
  return ristretto255.Point.fromBytes(bytes);
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i]!.toString(16).padStart(2, "0");
  }
  return s;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error("hex string must have even length");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = hex.slice(i * 2, i * 2 + 2);
    const n = parseInt(byte, 16);
    if (Number.isNaN(n) || !/^[0-9a-fA-F]{2}$/.test(byte)) {
      throw new Error(`invalid hex at offset ${i * 2}`);
    }
    out[i] = n;
  }
  return out;
}

export function sha256(bytes: Uint8Array): Uint8Array {
  return nobleSha256(bytes);
}

/**
 * Reduce a scalar to canonical [0, q) representation.
 */
export function normalizeScalar(s: Scalar): Scalar {
  return ((s % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER;
}

/**
 * Concatenate any number of byte chunks into one fresh Uint8Array.
 */
export function concatBytes(...chunks: ReadonlyArray<Uint8Array>): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

/**
 * Fiat-Shamir challenge: SHA-256 the byte string and reduce mod q.
 *
 * Reduction of a 256-bit hash mod q ≈ 2^252 introduces a bias of size
 * ≈ 2^-128 from uniform — acceptable for FS challenges (the security
 * loss vs a uniform-in-Z_q sample is below the symmetric-key security
 * margin we already accept everywhere else in the protocol).
 */
export function hashToScalar(bytes: Uint8Array): Scalar {
  const digest = sha256(bytes);
  let acc = 0n;
  for (let i = 0; i < digest.length; i++) {
    acc = (acc << 8n) | BigInt(digest[i]!);
  }
  return acc % GROUP_ORDER;
}

/**
 * Canonical JSON serialization, RFC 8785-style.
 *
 *  - object keys sorted lexicographically (recursively)
 *  - no whitespace
 *  - properties whose value is `undefined` are omitted; `null` is preserved
 *  - non-finite numbers (NaN, Infinity) and bigints are rejected
 *
 * The hash of the canonical JSON is what the DPO signs at close-of-poll
 * and what the verifier recomputes — any divergence in encoding silently
 * invalidates an entire bulletin, so this is centralized.
 */
export function canonicalJsonStringify(value: unknown): string {
  return canonicalize(value);
}

function canonicalize(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("canonical JSON does not allow NaN or Infinity");
    }
    return JSON.stringify(value);
  }
  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "bigint") {
    throw new Error(
      "canonical JSON cannot encode bigint; serialize to hex string first",
    );
  }
  if (Array.isArray(value)) {
    return "[" + value.map((v) => canonicalize(v)).join(",") + "]";
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const parts: string[] = [];
    for (const k of keys) {
      const v = obj[k];
      if (v === undefined) continue;
      parts.push(JSON.stringify(k) + ":" + canonicalize(v));
    }
    return "{" + parts.join(",") + "}";
  }
  throw new Error(`canonical JSON cannot encode value of type ${typeof value}`);
}
